import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchFiCategoriesThunk } from "../features/fip/fipThunk";
import { fetchDashboardDataThunk } from "../features/account/accountThunk";
import Button from "../components/ui/Button";

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { mobileNumber } = useAppSelector((s) => s.auth);
    const { linkedAccounts, loading } = useAppSelector((s) => s.account);

    useEffect(() => {
        dispatch(fetchFiCategoriesThunk());
        dispatch(fetchDashboardDataThunk());
    }, [dispatch]);

    // Calculate total net worth (mock balance for now, but based on real accounts if available)
    const totalNetWorth = linkedAccounts.length * 250000; // Mock calculation for demo

    return (
        <div className="min-h-screen bg-fin-bg flex flex-col">
            {/* Header */}
            <header className="bg-fin-surface border-b border-fin-border px-6 py-4 flex justify-between items-center sticky top-0 z-10">
                <div className="flex items-center">
                    <div className="h-8 w-8 bg-fin-600 rounded-lg flex items-center justify-center mr-3">
                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-fin-600 to-fin-800">CAMS Dashboard</span>
                </div>
                <div className="flex items-center space-x-4">
                    <button className="h-9 w-9 rounded-full bg-fin-bg flex items-center justify-center border border-fin-border hover:bg-fin-border transition-colors">
                        <svg className="h-5 w-5 text-fin-slate" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                    </button>
                    <div className="h-9 w-9 rounded-full bg-fin-100 flex items-center justify-center text-fin-700 font-bold border border-fin-200">
                        {mobileNumber?.slice(-2) || "AA"}
                    </div>
                </div>
            </header>

            <main className="flex-1 p-6 max-w-5xl mx-auto w-full">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-white">Welcome back!</h1>
                    <p className="text-fin-slate mt-1">Manage all your financial accounts in one secure place.</p>
                </div>

                {/* Account Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    <div className="bg-fin-surface p-6 rounded-2xl border border-fin-border hover:bg-fin-border/30 transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-2 bg-fin-500/10 rounded-lg">
                                <svg className="h-6 w-6 text-fin-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-sm font-medium text-fin-slate uppercase tracking-wider">Total Net Worth</p>
                        <h3 className="text-2xl font-bold text-white mt-1">₹ {totalNetWorth.toLocaleString()}</h3>
                        <p className="text-xs text-fin-primary font-medium mt-2 flex items-center">
                            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7 7 7M5 14l7 7 7-7" />
                            </svg>
                            +2.4% from last month
                        </p>
                    </div>

                    <div className="bg-fin-surface p-6 rounded-2xl border border-fin-border hover:bg-fin-border/30 transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-2 bg-fin-500/10 rounded-lg">
                                <svg className="h-6 w-6 text-fin-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-sm font-medium text-fin-slate uppercase tracking-wider">Linked Banks</p>
                        <h3 className="text-2xl font-bold text-white mt-1">{linkedAccounts.length} Banks</h3>
                        <p className="text-xs text-fin-slate mt-2">Active connections: 100%</p>
                    </div>

                    <div className="bg-fin-surface p-6 rounded-2xl border border-fin-border hover:bg-fin-border/30 transition-shadow flex flex-col justify-center items-center text-center">
                        <div className="h-10 w-10 bg-fin-500/10 rounded-full flex items-center justify-center mb-3">
                            <svg className="h-6 w-6 text-fin-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                        </div>
                        <h4 className="font-bold text-white mb-2">Connect New Bank</h4>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate("/selectAccountType")}
                            className="w-full"
                        >
                            Discover More
                        </Button>
                    </div>
                </div>

                {/* Linked Accounts List */}
                <div className="bg-fin-surface rounded-2xl border border-fin-border overflow-hidden mb-10">
                    <div className="px-6 py-5 border-b border-fin-border flex justify-between items-center">
                        <h3 className="font-bold text-white text-lg">My Accounts</h3>
                        <button className="text-fin-500 font-semibold text-sm hover:underline">View All</button>
                    </div>
                    <div className="divide-y divide-fin-border">
                        {loading && (
                            <div className="px-6 py-8 text-center text-fin-slate">
                                <div className="animate-spin h-8 w-8 border-4 border-fin-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                Loading your accounts...
                            </div>
                        )}

                        {!loading && linkedAccounts.length === 0 && (
                            <div className="px-6 py-8 text-center text-fin-slate">
                                No linked accounts found. Link your first bank to get started!
                            </div>
                        )}

                        {!loading && linkedAccounts.map((acc, idx) => (
                            <div key={idx} className="px-6 py-4 flex items-center hover:bg-fin-bg transition-colors group cursor-pointer">
                                <div className="h-12 w-12 bg-fin-bg rounded-xl flex items-center justify-center p-2 border border-fin-border mr-4">
                                    <img
                                        src={`https://uat.camsfinserv.com/newuat/assets/images/banks/${acc.FIPID || 'SBIN'}.png`}
                                        alt={acc.FIPNAME || "Bank"}
                                        className="h-full w-full object-contain"
                                        onError={(e) => {
                                            (e.target as any).src = "https://uat.camsfinserv.com/newuat/assets/images/banks/SBIN.png";
                                        }}
                                    />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-bold text-white group-hover:text-fin-500">{acc.FIPNAME || "Unknown Bank"}</h4>
                                    <p className="text-xs text-fin-slate font-medium">{acc.ACC_TYPE} • {acc.MASKED_ACC_NO}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-white">₹ {(Math.random() * 100000 + 10000).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    <p className="text-[10px] text-fin-slate font-medium uppercase tracking-wider mt-0.5">Updated just now</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Consents Section */}
                <div className="bg-fin-surface rounded-2xl border border-fin-border overflow-hidden mb-10">
                    <div className="px-6 py-5 border-b border-fin-border flex justify-between items-center">
                        <h3 className="font-bold text-white text-lg">Active Consents</h3>
                        <button className="text-fin-slate hover:text-white transition-colors">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                        </button>
                    </div>
                    <div className="p-6">
                        <div className="bg-fin-500/10 rounded-xl p-4 border border-fin-500/20 flex items-start">
                            <div className="p-2 bg-fin-500/20 rounded-lg mr-4">
                                <svg className="h-5 w-5 text-fin-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h5 className="font-bold text-white text-sm">Wealth Manager Portal</h5>
                                <p className="text-xs text-fin-slate mt-1 leading-relaxed">Active till 24 Oct 2026 • One-time fetch for Mutual Funds and Deposits data.</p>
                            </div>
                            <button className="text-xs font-bold text-fin-500 hover:bg-fin-500/20 px-3 py-1.5 rounded-lg transition-colors border border-fin-500/30">Manage</button>
                        </div>
                    </div>
                </div>

            </main>

            {/* Sticky Action Footer */}
            <div className="mt-auto bg-fin-surface border-t border-fin-border p-4 sticky bottom-0 z-10 lg:hidden">
                <Button
                    variant="primary"
                    fullWidth
                    onClick={() => navigate("/selectAccountType")}
                    className="py-3 font-bold"
                >
                    Link New Accounts
                </Button>
            </div>
        </div>
    );
};

export default Dashboard;

